import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Monitor, FileText, LogOut, Loader2, ShieldCheck, Users, Ticket } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const ADMIN_EMAILS = ['jedeelooi@gmail.com', 'staymiihospitality@gmail.com'];

const MODULE_CONFIG = {
  pms: {
    name: 'StayMii Lite',
    desc: 'A lightweight booking and record management platform. Manage bookings, rooms, guests, and records — designed for operators without an existing PMS.',
    icon: Monitor,
    path: '/dashboard',
    color: 'from-blue-600 to-blue-800',
  },
  einvoice: {
    name: 'StayMii e-Invoice',
    desc: 'Stay LHDN-compliant effortlessly. Auto-generate, validate, and submit e-invoices directly to MyInvois — purpose-built for Malaysian hotels and businesses.',
    icon: FileText,
    path: '/einvoice/portal/dashboard',
    color: 'from-indigo-600 to-indigo-800',
  },
  hr: {
    name: 'StayMii HR',
    desc: 'Digital hiring and onboarding system. Manage job applications, employee documents, and streamline your recruitment process.',
    icon: Users,
    path: '/hr/portal/applicants',
    color: 'from-emerald-600 to-emerald-800',
  },
  eticket: {
    name: 'StayMii E-Ticket',
    desc: 'Sell attraction tickets online with secure checkout. Issue unique QR e-tickets and admit guests instantly by scanning at the entrance.',
    icon: Ticket,
    path: '/eticket/admin',
    color: 'from-cyan-600 to-teal-700',
  },
};

export default function ModuleSelector() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('unified_token');
    if (!token) { navigate('/auth/login'); return; }
    axios.get(`${API_URL}/api/auth/unified/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { setUser(res.data.user); setLoading(false); })
      .catch(() => { navigate('/auth/login'); });
  }, [navigate]);

  const selectModule = async (module) => {
    setSelecting(module);
    const token = localStorage.getItem('unified_token');
    try {
      await axios.post(`${API_URL}/api/auth/unified/select-module`, { module }, { headers: { Authorization: `Bearer ${token}` } });
    } catch {}
    navigate(MODULE_CONFIG[module].path);
  };

  const handleLogout = () => {
    localStorage.removeItem('unified_token');
    localStorage.removeItem('unified_user');
    localStorage.removeItem('token');
    localStorage.removeItem('einvoice_token');
    navigate('/auth/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#2563EB] animate-spin" />
      </div>
    );
  }

  const modules = user?.modules || {};
  const enabled = Object.entries(modules).filter(([, v]) => v.enabled).map(([k]) => k);
  const isAdmin = ADMIN_EMAILS.includes((user?.email || '').toLowerCase());

  // Only show modules the account actually has enabled. Platform admins keep
  // access to E-Ticket for support; regular users see only what they signed up
  // for (previously E-Ticket was force-shown to everyone — a module leak).
  const allEnabled = [...enabled];
  if (isAdmin && !allEnabled.includes('eticket')) allEnabled.push('eticket');

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] flex items-center justify-center p-4" data-testid="module-selector-page">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <Link to="/">
            <h1 className="text-3xl font-bold text-white tracking-tight">Stay<span className="text-[#2563EB]">Mii</span></h1>
          </Link>
          <p className="text-slate-400 mt-2">Welcome back, <span className="text-white font-medium">{user?.full_name || user?.username}</span></p>
          <p className="text-slate-500 text-sm mt-1">Select a module to continue</p>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {allEnabled.map(mod => {
            const cfg = MODULE_CONFIG[mod];
            if (!cfg) return null;
            const Icon = cfg.icon;
            return (
              <button key={mod} onClick={() => selectModule(mod)} disabled={selecting === mod}
                className="text-left group" data-testid={`select-${mod}`}>
                <Card className="bg-white/5 border-white/10 hover:border-[#2563EB]/50 hover:bg-[#2563EB]/10 transition-all cursor-pointer group-disabled:opacity-60">
                  <CardContent className="p-6 flex items-start gap-5">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${cfg.color} flex items-center justify-center shrink-0`}>
                      {selecting === mod ? <Loader2 className="w-6 h-6 text-white animate-spin" /> : <Icon className="w-6 h-6 text-white" />}
                    </div>
                    <div>
                      <h2 className="text-white font-bold text-lg">{cfg.name}</h2>
                      <p className="text-slate-400 text-sm mt-1 leading-relaxed">{cfg.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              </button>
            );
          })}
        </div>

        <div className="text-center mt-8">
          {isAdmin && (
            <button onClick={() => navigate('/auth/admin/users')}
              className="text-[#2563EB] hover:text-blue-300 text-sm flex items-center gap-1.5 mx-auto transition-colors mb-4"
              data-testid="admin-users-link">
              <ShieldCheck className="w-3.5 h-3.5" /> User Management
            </button>
          )}
          <button onClick={handleLogout} className="text-slate-500 hover:text-white text-sm flex items-center gap-1.5 mx-auto transition-colors" data-testid="logout-btn">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
